import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CollectionCase, CaseNote, CaseAuditLog } from "@/types/collections";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  FileText,
  Activity,
  CreditCard,
  MessageSquare,
  Pencil,
  Save,
  X,
  History,
  ArrowRightLeft,
  Plus,
  Minus,
  Edit,
  UserPlus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface CaseDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseData: CollectionCase | null;
}

export function CaseDetailsDialog({ open, onOpenChange, caseData }: CaseDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<CollectionCase | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [notesHistory, setNotesHistory] = useState<CaseNote[]>([]);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Initialize edited data when caseData changes
  if (!caseData) return null;

  // Reset edit mode when dialog opens/closes or case changes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsEditMode(false);
      setEditedData(null);
      setSelectedTransactions(new Set());
    }
    onOpenChange(open);
  };

  const handleEditClick = () => {
    setEditedData({ ...caseData });
    setIsEditMode(true);
    // Initialize selected transactions with all transactions by default
    setSelectedTransactions(new Set(mockTransactions.map(t => t.id)));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSaveEdit = () => {
    if (!editedData) return;

    // Validate required fields
    if (!editedData.customerEmail || !editedData.customerPhone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedData.customerEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Get selected transaction details for logging/API call
    const selectedTransactionsList = Array.from(selectedTransactions);
    console.log("Selected transactions:", selectedTransactionsList);
    console.log("Total selected:", selectedTransactionsList.length);

    // Here you would typically make an API call to save the changes
    // including the selected transactions
    // For now, we'll just show a success message
    const message = selectedTransactionsList.length > 0 
      ? `Case updated successfully with ${selectedTransactionsList.length} transaction(s) selected`
      : "Case updated successfully";
    
    toast.success(message);
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());

    // In a real implementation, you would update the parent component's state
    // or refetch the data from the API
  };

  const currentData = isEditMode && editedData ? editedData : caseData;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  // Mock data for additional details
  const mockTransactions = [
    {
      id: "1",
      invoiceNumber: "INV-2024-001",
      invoiceDate: new Date(2024, 0, 15),
      dueDate: new Date(2024, 1, 15),
      amount: caseData.amount * 0.4,
      balance: caseData.amount * 0.4,
      status: "Overdue",
      daysOverdue: caseData.daysInCollection,
    },
    {
      id: "2",
      invoiceNumber: "INV-2024-002",
      invoiceDate: new Date(2024, 1, 1),
      dueDate: new Date(2024, 2, 1),
      amount: caseData.amount * 0.35,
      balance: caseData.amount * 0.35,
      status: "Overdue",
      daysOverdue: Math.max(0, caseData.daysInCollection - 15),
    },
    {
      id: "3",
      invoiceNumber: "INV-2024-003",
      invoiceDate: new Date(2024, 2, 10),
      dueDate: new Date(2024, 3, 10),
      amount: caseData.amount * 0.25,
      balance: caseData.amount * 0.25,
      status: "Outstanding",
      daysOverdue: 0,
    },
  ];

  const mockActivities = [
    {
      id: "1",
      type: "call",
      description: "Attempted phone call - no answer",
      outcome: "Left voicemail requesting callback",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "2",
      type: "email",
      description: "Sent payment reminder email",
      outcome: "Email delivered successfully",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 9, 15),
    },
    {
      id: "3",
      type: "note",
      description: "Customer requested payment plan options",
      outcome: "Sent payment plan proposal",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 11, 45),
    },
    {
      id: "4",
      type: "call",
      description: "Spoke with accounts payable manager",
      outcome: "Promised payment by end of week",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 12, 16, 20),
    },
  ];

  const mockCommunications = [
    {
      id: "1",
      type: "email",
      direction: "outbound",
      subject: "Payment Reminder - Account Overdue",
      content: "Dear Customer, This is a friendly reminder that your account has an outstanding balance...",
      sentAt: new Date(2024, 2, 18, 9, 15),
      sentBy: "Sarah Johnson",
      status: "delivered",
    },
    {
      id: "2",
      type: "email",
      direction: "inbound",
      subject: "RE: Payment Reminder",
      content: "Thank you for your email. We are experiencing temporary cash flow issues...",
      sentAt: new Date(2024, 2, 16, 14, 30),
      sentBy: caseData.customerName,
      status: "delivered",
    },
  ];

  // Mock notes history data - Initialize with some sample notes
  const mockNotesHistory: CaseNote[] = [
    {
      id: "1",
      caseId: caseData.caseId,
      content: "Customer called to discuss payment options. Mentioned they are waiting for a large client payment expected next week. Agreed to follow up on March 28th.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 21, 10, 30),
    },
    {
      id: "2",
      caseId: caseData.caseId,
      content: "Sent detailed payment plan proposal via email. Plan includes 3 monthly installments of $5,000 each. Awaiting customer response.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 14, 15),
    },
    {
      id: "3",
      caseId: caseData.caseId,
      content: "Initial contact made. Customer acknowledged the outstanding balance and requested time to review their cash flow situation. Scheduled follow-up call for next week.",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 9, 45),
    },
    {
      id: "4",
      caseId: caseData.caseId,
      content: "Case assigned to collections team. Customer has been unresponsive to previous payment reminders. Priority set to High due to amount and age of debt.",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];

  // Initialize notes history state with mock data if not already set
  if (notesHistory.length === 0 && open) {
    setNotesHistory(mockNotesHistory);
  }

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error("Please enter a note before saving");
      return;
    }

    setIsSavingNote(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Create new note
      const newNote: CaseNote = {
        id: `note-${Date.now()}`,
        caseId: caseData.caseId,
        content: newNoteContent.trim(),
        createdBy: "Current User", // In real app, this would be the logged-in user
        createdAt: new Date(),
      };

      // Add note to the beginning of the array (most recent first)
      setNotesHistory([newNote, ...notesHistory]);
      setNewNoteContent("");
      
      toast.success("Note added successfully");
    } catch (error) {
      toast.error("Failed to add note. Please try again.");
      console.error("Error adding note:", error);
    } finally {
      setIsSavingNote(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "call":
        return Phone;
      case "email":
        return Mail;
      case "note":
        return FileText;
      case "payment":
        return CreditCard;
      default:
        return Activity;
    }
  };

  // Mock audit log data - comprehensive case modification history
  const mockAuditLogs: CaseAuditLog[] = [
    {
      id: "audit-1",
      caseId: caseData.caseId,
      action: "status_change",
      description: "Case status changed",
      fieldName: "status",
      oldValue: "On Hold",
      newValue: "Active",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 22, 9, 15),
    },
    {
      id: "audit-2",
      caseId: caseData.caseId,
      action: "priority_change",
      description: "Priority level updated",
      fieldName: "priority",
      oldValue: "Medium",
      newValue: "High",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "audit-3",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "3",
      transactionNumber: "INV-2024-003",
      transactionAmount: caseData.amount * 0.25,
      transactionDate: new Date(2024, 2, 10),
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 11, 20),
    },
    {
      id: "audit-4",
      caseId: caseData.caseId,
      action: "field_update",
      description: "Customer contact information updated",
      fieldName: "customerEmail",
      oldValue: "old.email@example.com",
      newValue: caseData.customerEmail,
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 16, 45),
    },
    {
      id: "audit-5",
      caseId: caseData.caseId,
      action: "assignment_change",
      description: "Case reassigned to different collector",
      fieldName: "assignedTo",
      oldValue: "John Smith",
      newValue: "Sarah Johnson",
      createdBy: "System",
      createdAt: new Date(2024, 2, 15, 8, 30),
    },
    {
      id: "audit-6",
      caseId: caseData.caseId,
      action: "status_change",
      description: "Case status changed",
      fieldName: "status",
      oldValue: "Active",
      newValue: "On Hold",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 12, 13, 10),
    },
    {
      id: "audit-7",
      caseId: caseData.caseId,
      action: "transaction_removed",
      description: "Transaction removed from case",
      transactionId: "4",
      transactionNumber: "INV-2024-004",
      transactionAmount: 2500,
      transactionDate: new Date(2024, 1, 5),
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 11, 10, 15),
    },
    {
      id: "audit-8",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "2",
      transactionNumber: "INV-2024-002",
      transactionAmount: caseData.amount * 0.35,
      transactionDate: new Date(2024, 1, 1),
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 10, 14, 0),
    },
    {
      id: "audit-9",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "1",
      transactionNumber: "INV-2024-001",
      transactionAmount: caseData.amount * 0.4,
      transactionDate: new Date(2024, 0, 15),
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 10, 13, 45),
    },
    {
      id: "audit-10",
      caseId: caseData.caseId,
      action: "case_created",
      description: "Collection case created",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];

  const getAuditLogIcon = (action: CaseAuditLog['action']) => {
    switch (action) {
      case "status_change":
        return ArrowRightLeft;
      case "transaction_added":
        return Plus;
      case "transaction_removed":
        return Minus;
      case "priority_change":
        return AlertTriangle;
      case "assignment_change":
        return UserPlus;
      case "field_update":
        return Edit;
      case "note_added":
        return FileText;
      case "case_created":
        return History;
      default:
        return Activity;
    }
  };

  const getAuditLogColor = (action: CaseAuditLog['action']) => {
    switch (action) {
      case "status_change":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400";
      case "transaction_added":
        return "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400";
      case "transaction_removed":
        return "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400";
      case "priority_change":
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400";
      case "assignment_change":
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
      case "field_update":
        return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400";
      case "note_added":
        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400";
      case "case_created":
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
      default:
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl overflow-y-auto px-6">
        <SheetHeader>
          <div className="flex items-start justify-between pr-8">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle className="text-2xl">Case Details</SheetTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Complete information for case {caseData.caseId}
                  </p>
                </div>
                {!isEditMode ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditClick}
                    className="ml-4"
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 ml-4">
              <Badge variant={getPriorityBadgeVariant(currentData.priority)}>
                {currentData.priority} Priority
              </Badge>
              <Badge className={getStatusBadgeColor(currentData.status)}>
                {currentData.status}
              </Badge>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="notes">Notes History</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[calc(90vh-240px)] mt-4">
            <TabsContent value="overview" className="space-y-4">
              {/* Customer Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Customer Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Customer Name</p>
                    <p className="text-base font-semibold">{currentData.customerName}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Customer ID</p>
                    <p className="text-base">{currentData.customerId}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    {isEditMode && editedData ? (
                      <Input
                        id="email"
                        type="email"
                        value={editedData.customerEmail}
                        onChange={(e) => setEditedData({ ...editedData, customerEmail: e.target.value })}
                        placeholder="customer@example.com"
                      />
                    ) : (
                      <p className="text-base">{currentData.customerEmail}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      Phone
                    </Label>
                    {isEditMode && editedData ? (
                      <Input
                        id="phone"
                        type="tel"
                        value={editedData.customerPhone}
                        onChange={(e) => setEditedData({ ...editedData, customerPhone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    ) : (
                      <p className="text-base">{currentData.customerPhone}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Case Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Case Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Case ID</p>
                    <p className="text-base font-semibold">{currentData.caseId}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      Outstanding Amount
                    </p>
                    <p className="text-base font-semibold text-red-600 dark:text-red-400">
                      ${currentData.amount.toLocaleString()}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Days in Collection
                    </p>
                    <p className="text-base">{currentData.daysInCollection} days</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Activity className="h-4 w-4" />
                      Collection Strategy
                    </p>
                    <p className="text-base font-semibold">{currentData.strategy.name}</p>
                    <p className="text-xs text-muted-foreground">{currentData.strategy.type}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="riskLevel" className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      Risk Level
                    </Label>
                    {isEditMode && editedData ? (
                      <Select
                        value={editedData.riskLevel}
                        onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                          setEditedData({ ...editedData, riskLevel: value })
                        }
                      >
                        <SelectTrigger id="riskLevel">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getPriorityBadgeVariant(currentData.riskLevel)}>
                        {currentData.riskLevel}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority" className="text-sm font-medium text-muted-foreground">
                      Priority
                    </Label>
                    {isEditMode && editedData ? (
                      <Select
                        value={editedData.priority}
                        onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                          setEditedData({ ...editedData, priority: value })
                        }
                      >
                        <SelectTrigger id="priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getPriorityBadgeVariant(currentData.priority)}>
                        {currentData.priority}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status" className="text-sm font-medium text-muted-foreground">
                      Status
                    </Label>
                    {isEditMode && editedData ? (
                      <Select
                        value={editedData.status}
                        onValueChange={(value: 'Active' | 'On Hold' | 'Closed') => 
                          setEditedData({ ...editedData, status: value })
                        }
                      >
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getStatusBadgeColor(currentData.status)}>
                        {currentData.status}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Next Action */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Next Action
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{caseData.nextAction}</p>
                      <p className="text-sm text-muted-foreground">
                        Due: {format(new Date(caseData.nextActionDate), "MMMM d, yyyy")}
                      </p>
                    </div>
                    <Button>Complete Action</Button>
                  </div>
                </CardContent>
              </Card>

              {(currentData.notes || isEditMode) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isEditMode && editedData ? (
                      <Textarea
                        id="notes"
                        value={editedData.notes || ''}
                        onChange={(e) => setEditedData({ ...editedData, notes: e.target.value })}
                        placeholder="Add notes about this case..."
                        rows={4}
                      />
                    ) : (
                      <p className="text-sm">{currentData.notes}</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="transactions" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Outstanding Invoices</CardTitle>
                      <CardDescription>
                        {isEditMode 
                          ? `Select transactions to include in this case (${selectedTransactions.size} selected)`
                          : "All invoices associated with this collection case"
                        }
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isEditMode && <TableHead className="w-12"></TableHead>}
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Invoice Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockTransactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          className={cn(
                            isEditMode && "cursor-pointer hover:bg-muted/50",
                            isEditMode && selectedTransactions.has(transaction.id) && "bg-muted/30"
                          )}
                          onClick={() => isEditMode && handleToggleTransaction(transaction.id)}
                        >
                          {isEditMode && (
                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedTransactions.has(transaction.id)}
                                onCheckedChange={() => handleToggleTransaction(transaction.id)}
                                aria-label={`Select ${transaction.invoiceNumber}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">
                            {transaction.invoiceNumber}
                          </TableCell>
                          <TableCell>
                            {format(transaction.invoiceDate, "MMM d, yyyy")}
                          </TableCell>
                          <TableCell>
                            {format(transaction.dueDate, "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            ${transaction.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ${transaction.balance.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.status === "Overdue"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {transaction.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {transaction.daysOverdue > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                {transaction.daysOverdue}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      ${caseData.amount.toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>
                    Complete history of all actions taken on this case
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockActivities.map((activity, index) => {
                      const Icon = getActivityIcon(activity.type);
                      return (
                        <div key={activity.id} className="relative">
                          {index !== mockActivities.length - 1 && (
                            <div className="absolute left-4 top-8 h-full w-0.5 bg-border" />
                          )}
                          <div className="flex gap-4">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                              <Icon className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{activity.description}</p>
                                <p className="text-xs text-muted-foreground">
                                  {format(activity.createdAt, "MMM d, yyyy h:mm a")}
                                </p>
                              </div>
                              {activity.outcome && (
                                <p className="text-sm text-muted-foreground">
                                  {activity.outcome}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                by {activity.createdBy}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Notes History</CardTitle>
                  <CardDescription>
                    {isEditMode 
                      ? "Add new notes and view chronological history of all notes for this case"
                      : "Chronological history of all notes for this case"
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Note Section - Only visible in Edit Mode */}
                  {isEditMode && (
                    <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                      <Label htmlFor="newNote" className="text-sm font-medium">
                        Add New Note
                      </Label>
                      <Textarea
                        id="newNote"
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        placeholder="Enter your note here..."
                        rows={4}
                        className="resize-none"
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddNote}
                          disabled={isSavingNote || !newNoteContent.trim()}
                          size="sm"
                        >
                          {isSavingNote ? (
                            <>
                              <Clock className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Add Note
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Notes History Display */}
                  {notesHistory.length > 0 ? (
                    <div className="space-y-4">
                      <Separator />
                      <div className="space-y-4">
                        {notesHistory.map((note, index) => (
                          <div key={note.id} className="relative">
                            {index !== notesHistory.length - 1 && (
                              <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                            )}
                            <div className="flex gap-4">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <p className="text-sm text-foreground leading-relaxed">
                                      {note.content}
                                    </p>
                                  </div>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                                    {format(note.createdAt, "MMM d, yyyy h:mm a")}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    <User className="h-3 w-3 mr-1" />
                                    {note.createdBy}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No notes have been added to this case yet.</p>
                      {isEditMode && (
                        <p className="text-xs mt-1">Use the form above to add your first note.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="communications" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Communication History</CardTitle>
                  <CardDescription>
                    All emails and messages exchanged with the customer
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockCommunications.map((comm) => (
                      <Card key={comm.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant={
                                    comm.direction === "outbound" ? "default" : "secondary"
                                  }
                                >
                                  {comm.direction === "outbound" ? "Sent" : "Received"}
                                </Badge>
                                <Badge variant="outline">{comm.type}</Badge>
                              </div>
                              <CardTitle className="text-base">{comm.subject}</CardTitle>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {format(comm.sentAt, "MMM d, yyyy h:mm a")}
                            </p>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">{comm.content}</p>
                          <Separator className="my-2" />
                          <p className="text-xs text-muted-foreground">
                            {comm.direction === "outbound" ? "Sent by" : "From"}: {comm.sentBy}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Complete chronological history of all case modifications and changes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockAuditLogs.map((log, index) => {
                      const Icon = getAuditLogIcon(log.action);
                      const colorClass = getAuditLogColor(log.action);
                      
                      return (
                        <div key={log.id} className="relative">
                          {index !== mockAuditLogs.length - 1 && (
                            <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                          )}
                          <div className="flex gap-4">
                            <div className={cn(
                              "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
                              colorClass
                            )}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-1">
                                  <p className="font-medium text-sm">{log.description}</p>
                                  
                                  {/* Status/Priority/Field Changes */}
                                  {(log.action === "status_change" || log.action === "priority_change" || log.action === "field_update" || log.action === "assignment_change") && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Badge variant="outline" className="text-xs">
                                        {log.oldValue || "None"}
                                      </Badge>
                                      <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                      <Badge variant="outline" className="text-xs">
                                        {log.newValue}
                                      </Badge>
                                    </div>
                                  )}
                                  
                                  {/* Transaction Added */}
                                  {log.action === "transaction_added" && log.transactionNumber && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{log.transactionNumber}</span>
                                        <span>•</span>
                                        <span className="font-medium text-green-600 dark:text-green-400">
                                          ${log.transactionAmount?.toLocaleString()}
                                        </span>
                                      </div>
                                      {log.transactionDate && (
                                        <div className="text-xs">
                                          Invoice Date: {format(log.transactionDate, "MMM d, yyyy")}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Transaction Removed */}
                                  {log.action === "transaction_removed" && log.transactionNumber && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{log.transactionNumber}</span>
                                        <span>•</span>
                                        <span className="font-medium text-red-600 dark:text-red-400">
                                          ${log.transactionAmount?.toLocaleString()}
                                        </span>
                                      </div>
                                      {log.transactionDate && (
                                        <div className="text-xs">
                                          Invoice Date: {format(log.transactionDate, "MMM d, yyyy")}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground whitespace-nowrap">
                                  {format(log.createdAt, "MMM d, yyyy h:mm a")}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  <User className="h-3 w-3 mr-1" />
                                  {log.createdBy}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {mockAuditLogs.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No audit log entries found for this case.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
